import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  selectUpgradeState,
  setClientSecret,
} from '@/app/redux/slices/upgradeSlice';
import { OrganisationMember } from '@/common/types/organisation';
import { DataLoader } from '@/common/ui/LoadingSpinners';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { getPriceId, PLAN_DETAILS } from '@/common/utils/license';
import { isUserAdmin } from '@/common/utils/organisation';
import stripePromise from '@/common/utils/stripe';
import api from '@/db/apiClient';
import {
  CaretLeftIcon,
  InfoIcon,
  NumberCircleOneIcon,
  NumberCircleTwoIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@shadcn/components/alert-dialog';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance } from '@stripe/stripe-js';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#7571ad',
  },
};

export default function CheckoutLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const step = location.pathname.split('/').pop();

  const { organisation } = useOrganisation();

  const userId = useAppSelector(selectUserId);
  const {
    seatCount,
    selectedPlan,
    billingCycle,
    clientSecret,
    currency,
    coupon,
  } = useAppSelector(selectUpgradeState);

  const stripeCustomerId = organisation?.license.stripeCustomerId;
  const isAdmin = isUserAdmin(organisation, userId);

  const [loading, setLoading] = useState(true);

  const adminMembers = useMemo(() => {
    return (organisation?.members ?? [])
      .reduce<OrganisationMember[]>((acc, m) => {
        return isUserAdmin(organisation, m.uid) ? [...acc, m] : acc;
      }, [])
      .sort((a, b) => a.createdAt - b.createdAt)
      .slice(0, 2);
  }, [organisation]);

  useEffect(() => {
    if (!seatCount || seatCount < 1) {
      navigate('/pricing');
    }
  }, [seatCount, navigate, location.pathname]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      if (!stripeCustomerId || !seatCount || !selectedPlan || !billingCycle) {
        console.error('Missing required fields for subscription');
        return;
      }
      const planSeats = PLAN_DETAILS[selectedPlan].seatCount;
      const quantity = Math.max(planSeats, seatCount);
      try {
        const { clientSecret } = await api.asEducator.createSubscription({
          customerId: stripeCustomerId,
          priceId: getPriceId(selectedPlan, billingCycle, currency),
          seatCount: quantity,
          couponId: coupon?.id,
        });
        dispatch(setClientSecret(clientSecret));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    dispatch,
    isAdmin,
    billingCycle,
    selectedPlan,
    seatCount,
    stripeCustomerId,
    currency,
    coupon,
  ]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <DataLoader />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-8 md:p-8 bg-neutral-100">
      <div className="w-full max-w-7xl">
        <div
          className="flex items-center gap-2 hover:underline cursor-pointer text-sm text-muted-foreground mb-4"
          onClick={() => navigate('/dashboard/settings/billing')}
        >
          <CaretLeftIcon />
          <span>Back to Billing & Subscription</span>
        </div>

        <div className="flex flex-col bg-white p-4 md:p-8 rounded-lg w-full shadow-lg border border-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full text-neutral-700 mb-4 tracking-tight">
            <span className="text-lg md:text-xl font-medium flex gap-2 items-center">
              <NumberCircleOneIcon
                weight={step === 'payment-details' ? 'fill' : 'regular'}
                className="text-primary"
                size={24}
              />
              <span>Payment Details</span>
            </span>
            <Separator className="hidden sm:block w-[50px]" />
            <span className="text-lg md:text-xl font-medium flex gap-2 items-center">
              <NumberCircleTwoIcon
                weight={step === 'confirmation' ? 'fill' : 'regular'}
                className="text-primary"
                size={24}
              />
              <span>Confirmation</span>
            </span>
          </div>

          <Separator className="mb-8" />
          <div className="w-full h-full">
            {isAdmin && clientSecret ? (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance,
                }}
              >
                <Outlet />
              </Elements>
            ) : isAdmin ? (
              <Alert variant="destructive">
                <WarningCircleIcon size={18} className="ml-2" />
                {UNKNOWN_ERROR_MESSAGE}
              </Alert>
            ) : (
              <div className="w-full h-full flex gap-6 relative p-4">
                <div className="flex flex-col gap-4 w-2/3 blur-md">
                  <div className="h-8 w-40 bg-gray-100" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full" />
                  <div className="h-8 w-40 bg-gray-100" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-gray-100 rounded-md" />
                </div>
                <div className="flex flex-col gap-4 w-1/3 blur-md">
                  <div className="h-full bg-gray-100 rounded-md" />
                  <div className="h-8 w-full bg-primary rounded-md" />
                </div>

                <AlertDialog open={true}>
                  <AlertDialogContent>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <InfoIcon className="size-4" weight="bold" /> Payment can
                      only be completed by an administrator
                    </AlertDialogTitle>
                    <AlertDialogDescription className="flex flex-col gap-2">
                      <span>
                        Please get in contact with your organisation's
                        administrator(s) to finish setting up your Spellcaster
                        plan.
                      </span>
                      <span>
                        Here are the contact details for your admin(s):
                      </span>
                      <div className="flex flex-col py-2 px-4 border border-border rounded-md">
                        {adminMembers.map((m) => (
                          <div className="flex items-center gap-2">
                            <span className="text-xl">•</span>
                            <div className="flex flex-col">
                              <span>{m.name}</span>
                              <span className="text-sm">{m.email}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AlertDialogDescription>

                    <AlertDialogFooter>
                      <Button
                        onClick={() => navigate(`/pricing`)}
                        className="w-full"
                      >
                        Okay
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
